import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Stethoscope,
  LogOut,
  User,
  Users,
  ShieldCheck,
  LayoutDashboard,
  Stethoscope as StethoIcon,
  UserRound,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const PANEL_BY_ROLE = {
  paciente: "/dashboard",
  medico: "/medico",
  admin: "/admin",
};

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const panelPath = user ? PANEL_BY_ROLE[user.role] || "/" : null;

  return (
    <header
      className="border-b border-border bg-white sticky top-0 z-30"
      data-testid="site-header"
    >
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
          <div className="w-9 h-9 rounded-lg bg-[#0F2942] text-white grid place-items-center group-hover:bg-[#123553] transition-colors">
            <Stethoscope size={18} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-semibold text-[#0F2942] tracking-tight">
              Siempre Salud
            </div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground -mt-0.5">
              Orientación clínica IA
            </div>
          </div>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2">
          <Link to="/directorio" data-testid="header-directorio-link">
            <Button variant="ghost" size="sm">
              <Users size={14} /> Directorio
            </Button>
          </Link>
          {user && user.role === "admin" ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white gap-1"
                  data-testid="nav-panel-switcher"
                >
                  <LayoutDashboard size={14} /> Mis paneles
                  <ChevronDown size={12} className="opacity-80" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-[11px] uppercase tracking-widest text-muted-foreground">
                  Cambiar de panel
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => navigate("/admin")}
                  data-testid="panel-switcher-admin"
                  className="cursor-pointer"
                >
                  <ShieldCheck size={14} className="text-[#0F2942]" />
                  <span>Panel Admin</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => navigate("/dashboard")}
                  data-testid="panel-switcher-paciente"
                  className="cursor-pointer"
                >
                  <UserRound size={14} className="text-[#2563EB]" />
                  <span>Panel Persona usuaria</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => navigate("/medico")}
                  data-testid="panel-switcher-medico"
                  className="cursor-pointer"
                >
                  <StethoIcon size={14} className="text-[#0F2942]" />
                  <span>Panel Especialista</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            user && panelPath && (
              <Link to={panelPath} data-testid="nav-dashboard-btn">
                <Button
                  size="sm"
                  className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
                >
                  <LayoutDashboard size={14} /> Mi panel
                </Button>
              </Link>
            )
          )}
          {user ? (
            <>
              <div
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-muted"
                data-testid="user-badge"
              >
                <User size={14} className="text-[#2563EB]" />
                <span className="text-sm font-medium text-foreground">
                  {user.nombre_completo}
                </span>
                <span
                  className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                    user.role === "medico"
                      ? "bg-[#0F2942] text-white"
                      : "bg-[#2563EB] text-white"
                  }`}
                  data-testid="user-role"
                >
                  {user.role === "paciente"
                    ? "persona usuaria"
                    : user.role === "medico"
                    ? "especialista"
                    : user.role}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // Navigate first so ProtectedRoute doesn't intercept during unmount
                  // and redirect to /login before we reach the landing page.
                  navigate("/", { replace: true });
                  logout();
                }}
                data-testid="logout-btn"
              >
                <LogOut size={14} /> Cerrar sesión
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="header-login-link">
                  Iniciar sesión
                </Button>
              </Link>
              <Link to="/registro">
                <Button
                  size="sm"
                  className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
                  data-testid="header-register-link"
                >
                  Registrarse
                </Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="mt-16 border-t border-border bg-white" data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-6 py-8 text-xs text-muted-foreground grid gap-3 sm:grid-cols-2">
        <div>
          <div className="font-semibold text-foreground mb-1">Siempre Salud</div>
          <p className="max-w-md leading-relaxed">
            Servicio de orientación en salud basado en Guías de Práctica Clínica (GPC) de México.
            Recomendaciones limitadas a medicamentos de venta libre (COFEPRIS V y VI).
            No sustituye la valoración médica presencial.
          </p>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
            <Link
              to="/aviso-de-privacidad"
              className="text-[#2563EB] hover:underline"
              data-testid="footer-aviso-link"
            >
              Aviso de Privacidad
            </Link>
            <Link
              to="/terminos-y-condiciones"
              className="text-[#2563EB] hover:underline"
              data-testid="footer-terminos-link"
            >
              Términos y Condiciones
            </Link>
          </div>
        </div>
        <div className="sm:text-right">
          <div className="uppercase tracking-widest text-[10px] mb-1">Cumplimiento</div>
          <div>NOM-004-SSA3-2012 · LFPDPPP · COFEPRIS · GPC MX</div>
          <div className="mt-2">© {new Date().getFullYear()} Siempre Salud</div>
        </div>
      </div>
    </footer>
  );
}
